import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

function LegalLayout({ title, sections }) {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="legal-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center space-x-2 text-slate-700 hover:text-blue-600 transition-colors">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-extrabold">Redoc QMS 200–230</span>
          </Link>
          <Link to="/" data-testid="legal-back-link" className="text-xs font-semibold text-slate-500 hover:text-blue-600 transition-colors">← Tillbaka till systemet</Link>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          <h1 className="text-2xl font-extrabold text-slate-900">{title}</h1>
          <p className="text-xs text-slate-400 mt-1 mb-6">Senast uppdaterad: 5 september 2026</p>
          {sections.map((s, i) => (
            <div key={i} className="mb-5">
              <h2 className="text-sm font-bold text-slate-800 mb-1.5">{i + 1}. {s.h}</h2>
              {s.p.map((par, j) => (
                <p key={j} className="text-sm text-slate-600 leading-relaxed mb-1.5">{par}</p>
              ))}
            </div>
          ))}
          <div className="border-t border-slate-100 pt-4 mt-6 text-xs text-slate-500">
            Kontakt: <a href="mailto:contact@dynastyconsult.com" className="text-blue-600 hover:underline">contact@dynastyconsult.com</a> · Redoc TM AB
          </div>
        </div>
      </div>
    </div>
  );
}

const TERMS = [
  { h: "Tjänsten", p: [
    "Redoc QMS 200–230 tillhandahålls av Redoc TM AB som en webbaserad tjänst för kvalitetsstyrning enligt FAR Reko 200–230, riktad till redovisnings- och revisionsbyråer.",
    "Tjänsten omfattar moduler för verksamhetsanalys, riskbedömning, riskreducerande åtgärder, avvikelsehantering, årlig utvärdering samt oföränderlig kvalitetslogg (audit trail).",
  ]},
  { h: "Prenumeration och uppsägning", p: [
    "Tjänsten tecknas som årsabonnemang med löpande förnyelse per tolvmånadersperiod.",
    "Uppsägningstiden är en (1) månad. Efter uppsägning behåller kunden full åtkomst under uppsägningstiden. När uppsägningstiden löpt ut stängs inloggningen automatiskt för samtliga användare i organisationen.",
  ]},
  { h: "Konton och säkerhet", p: [
    "Kunden ansvarar för att användarkonton hanteras av behörig personal (t.ex. supervisor eller kvalitetsansvarig) och att inloggningsuppgifter hålls hemliga.",
    "Misstänkt obehörig åtkomst ska omedelbart rapporteras till Redoc TM AB.",
  ]},
  { h: "Kundens ansvar", p: [
    "Kunden ansvarar för att uppgifter som matas in i systemet är korrekta och att systemet används i enlighet med FAR:s krav och tillämplig lagstiftning.",
    "Systemet är ett stödverktyg — ansvaret för kvalitetsstyrningen enligt Reko kvarstår alltid hos byrån.",
  ]},
  { h: "Immateriella rättigheter", p: [
    "Alla rättigheter till tjänsten, inklusive programvara, design och dokumentation, tillhör Redoc TM AB. Kunden får en icke-exklusiv rätt att använda tjänsten under prenumerationstiden.",
  ]},
  { h: "Ansvarsbegränsning", p: [
    "Redoc TM AB ansvarar inte för indirekta skador, utebliven vinst eller skador som uppstår till följd av att kunden inte fullgjort sina egna kvalitetsstyrningsåtgärder.",
  ]},
  { h: "Ändringar och tillämplig lag", p: [
    "Redoc TM AB kan uppdatera dessa villkor. Väsentliga ändringar meddelas i systemet minst 30 dagar innan de träder i kraft.",
    "Svensk rätt är tillämplig. Tvister avgörs i första hand genom förhandling, i andra hand av svensk allmän domstol.",
  ]},
];

const PRIVACY = [
  { h: "Personuppgiftsansvarig", p: [
    "Redoc TM AB är personuppgiftsansvarig för behandlingen av personuppgifter i tjänsten Redoc QMS 200–230. Kontakt: contact@dynastyconsult.com.",
  ]},
  { h: "Vilka uppgifter vi behandlar", p: [
    "Kontouppgifter: namn, e-postadress, roll och signeringstidsstämplar för byråns användare.",
    "Loggdata: händelser i kvalitetsloggen (audit trail) med vem, vad och när.",
    "Innehåll: uppladdade bilagor och dokumentation som kunden själv matar in.",
  ]},
  { h: "Ändamål och laglig grund", p: [
    "Uppgifterna behandlas för att tillhandahålla, säkra och förbättra tjänsten (laglig grund: fullgörande av avtal), samt för att uppfylla kvalitetsdokumentationskrav i kundens verksamhet (laglig grund: berättigat intresse).",
  ]},
  { h: "Lagringstid och mottagare", p: [
    "Uppgifterna lagras under prenumerationstiden. Efter avslutad prenumeration raderas eller anonymiseras personuppgifterna inom 90 dagar, om inte annat avtalats.",
    "Uppgifter delas aldrig med tredje part för marknadsföring. Drift- och lagringsleverantörer (personuppgiftsbiträden) används inom EU/EES.",
  ]},
  { h: "Dina rättigheter", p: [
    "Du har rätt till tillgång, rättelse, radering, begränsning av behandling, dataportabilitet samt att invända mot behandling. Kontakta oss på contact@dynastyconsult.com.",
    "Du har även rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY).",
  ]},
  { h: "Säkerhet", p: [
    "Vi tillämpar lämpliga tekniska och organisatoriska åtgärder: krypterad överföring (HTTPS), hashade lösenord (bcrypt), rollbaserad åtkomstkontroll och oföränderlig loggning.",
  ]},
];

const COOKIES = [
  { h: "Vad är cookies?", p: [
    "Cookies är små textfiler som lagras i din webbläsare för att webbplatsen ska fungera och komma ihåg dina val.",
  ]},
  { h: "Nödvändiga cookies vi använder", p: [
    "access_token och refresh_token — httpOnly-sessionscookies som krävs för inloggning och säker åtkomst till systemet. Dessa kan inte stängas av utan att tjänsten slutar fungera.",
    "cookie_consent (localStorage) — sparar ditt val i cookie-bannern så att du inte behöver svara igen vid varje besök.",
  ]},
  { h: "Inga spårningscookies", p: [
    "Redoc QMS använder inga analys-, spårnings- eller marknadsföringscookies och inga tredjepartscookies.",
  ]},
  { h: "Hantera cookies", p: [
    "Du kan när som helst radera eller blockera cookies i din webbläsares inställningar. Observera att inloggningen då inte fungerar.",
    "Vill du ändra ditt samtycke? Radera webbplatsdata för denna domän i webbläsaren — bannern visas då igen vid nästa besök.",
  ]},
];

export function TermsPage() {
  return <LegalLayout title="Användarvillkor" sections={TERMS} />;
}

export function PrivacyPage() {
  return <LegalLayout title="Integritetspolicy" sections={PRIVACY} />;
}

export function CookiesPage() {
  return <LegalLayout title="Cookiepolicy" sections={COOKIES} />;
}
